import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Cache des redirections en mémoire pour éviter les appels DB répétés
const redirectCache = new Map<string, string | null>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cacheTimestamps = new Map<string, number>();

/**
 * Hook optimisé pour gérer les redirections 301 automatiques
 * Utilise un cache en mémoire pour réduire les appels à la base de données
 */
export const useURLRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const checkedPaths = useRef(new Set<string>());

  useEffect(() => {
    const checkAndRedirect = async () => {
      const currentPath = location.pathname;
      
      // Éviter de vérifier le même chemin plusieurs fois dans la session
      if (checkedPaths.current.has(currentPath)) {
        return;
      }

      // Vérifier le cache en mémoire
      const now = Date.now();
      const cacheTime = cacheTimestamps.get(currentPath);
      
      if (cacheTime && (now - cacheTime) < CACHE_DURATION) {
        const cachedRedirect = redirectCache.get(currentPath);
        if (cachedRedirect) {
          console.log(`🔀 Redirection 301 (cached): ${currentPath} → ${cachedRedirect}`);
          navigate(cachedRedirect, { 
            replace: true,
            state: { redirectedFrom: currentPath }
          });
        }
        checkedPaths.current.add(currentPath);
        return;
      }

      // Vérifier dans la base de données
      try {
        const { data: redirect } = await supabase
          .rpc('increment_redirect_hit', { p_old_path: currentPath })
          .single();

        // Mettre à jour le cache
        cacheTimestamps.set(currentPath, now);
        
        if (redirect?.new_path) {
          redirectCache.set(currentPath, redirect.new_path);
          console.log(`🔀 Redirection 301: ${currentPath} → ${redirect.new_path}`);
          
          navigate(redirect.new_path, { 
            replace: true,
            state: { redirectedFrom: currentPath }
          });
        } else {
          // Pas de redirection trouvée, mettre null dans le cache
          redirectCache.set(currentPath, null);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de redirection:', error);
        // En cas d'erreur, marquer comme vérifié pour éviter les tentatives répétées
        redirectCache.set(currentPath, null);
      }

      checkedPaths.current.add(currentPath);
    };

    checkAndRedirect();
  }, [location.pathname, navigate]);
};
